import { z } from "zod";
import { LOCATION_CODE_MESSAGE, parseLocationCode } from "../location-labels.js";
import { ROLE_CODES } from "../types/index.js";
export const locationCodeSchema = z.string().trim().transform((value, ctx) => {
    const parsed = parseLocationCode(value);
    if (!parsed) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: LOCATION_CODE_MESSAGE });
        return z.NEVER;
    }
    return parsed.code;
});
export const digit8Schema = z.string().regex(/^\d{8}$/);
export const loginSchema = z.object({
    employeeNo: z.string().trim().min(1).max(64),
    password: z.string().min(1).max(128),
    client: z.enum(["console", "station"]).default("console"),
    deviceId: z.string().max(128).optional(),
});
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1).max(128),
    newPassword: z.string().min(8).max(128),
});
export const PERSON_NAME_MAX_LENGTH = 40;
export const EMPTY_PERSON_NAME_MESSAGE = "姓名没有改成。这一栏还空着，请写上姓名后再保存。";
export const LONG_PERSON_NAME_MESSAGE = `姓名没有改成。超过 ${PERSON_NAME_MAX_LENGTH} 个字了，请缩短后再保存。`;
export const personNameSchema = z
    .string({
    required_error: EMPTY_PERSON_NAME_MESSAGE,
    invalid_type_error: EMPTY_PERSON_NAME_MESSAGE,
})
    .trim()
    .min(1, EMPTY_PERSON_NAME_MESSAGE)
    .max(PERSON_NAME_MAX_LENGTH, LONG_PERSON_NAME_MESSAGE);
export const createUserSchema = z.object({
    employeeNo: z.string().trim().min(1).max(64),
    name: personNameSchema,
    role: z.enum(ROLE_CODES),
    password: z.string().min(8).max(128),
});
export const renameUserSchema = z.object({
    name: personNameSchema,
});
export const resetPasswordSchema = z.object({
    password: z.string().min(8).max(128),
});
export const changeUserRoleSchema = z.object({
    role: z.enum(ROLE_CODES),
});
export const createSkuSchema = z.object({
    // 完整 SKU，含颜色和尺码，例如 ST1920white34。不是不含色码的货号。
    humanCode: z.string().trim().min(1).max(64),
    name: z.string().trim().min(1).max(120),
    color: z.string().trim().min(1).max(40),
    sizeUs: z.string().trim().min(1).max(20),
    sizeEu: z.string().trim().min(1).max(20),
    trackUnit: z.boolean(),
});
export const updateSkuSchema = createSkuSchema;
export const createBatchSchema = z.object({
    skuId: z.string().min(1),
    inboundDate: z.string().min(1),
    factoryName: z.string().trim().max(80).optional(),
    plannedQty: z.number().int().min(1).max(100000),
    qcNote: z.string().max(500).optional(),
});
export const lockArrivalSchema = z.object({
    spareRatio: z.number().int().min(10).max(20),
});
export const activateSchema = z.object({
    scannedCode: z.string().trim().min(1).max(120),
    printTaskId: z.string().min(1).optional(),
    arrivalNo: z.string().trim().min(1).max(40).optional(),
});
export const putawayResolveSchema = z.object({
    scannedCode: z.string().trim().min(1).max(120),
});
export const putawaySchema = z.object({
    locationCode: locationCodeSchema,
    batchId: z.string().min(1),
});
export const correctLocationSchema = z.object({
    batchId: z.string().min(1),
    newLocationCode: locationCodeSchema,
    password: z.string().min(1).max(128),
});
export const updateLocationSchema = z.object({
    capacity: z.number().int().min(1).max(100000),
    status: z.enum(["NORMAL", "FULL", "LOCKED"]),
    password: z.string().min(1).max(128),
});
const locationZoneSchema = z.string().regex(/^[A-Z]$/).nullish();
const locationRackSchema = z.string().regex(/^\d{2}$/).nullish();
const locationSpanNumber = z.number().int().min(1).max(99).nullish();
export const locationGenerateSchema = z.object({
    zone: z.string().regex(/^[A-Z]$/),
    rackFrom: z.number().int().min(1).max(99),
    rackTo: z.number().int().min(1).max(99),
    shelfFrom: locationSpanNumber,
    shelfTo: locationSpanNumber,
    binFrom: locationSpanNumber,
    binTo: locationSpanNumber,
    capacity: z.number().int().min(1).max(100000).default(50),
});
export const locationBatchSchema = z
    .object({
    zone: locationZoneSchema,
    rack: locationRackSchema,
    shelf: locationRackSchema,
    capacity: z.number().int().min(1).max(100000).optional(),
    status: z.enum(["NORMAL", "LOCKED"]).optional(),
    password: z.string().min(1).max(128),
})
    .superRefine((value, ctx) => {
    const hasCapacity = value.capacity !== undefined;
    const hasStatus = value.status !== undefined;
    if (hasCapacity === hasStatus) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "请只改容量，或只改成锁定或正常。" });
    }
});
export const locationDeleteSchema = z.object({
    zone: locationZoneSchema,
    rack: locationRackSchema,
    shelf: locationRackSchema,
    code: locationCodeSchema.optional(),
    password: z.string().min(1).max(128),
});
export const createOutboundSchema = z.object({
    channel: z.string().trim().min(1).max(40),
    targetWarehouse: z.string().trim().min(1).max(40),
    skuId: z.string().min(1),
    requiredQty: z.number().int().min(1).max(100000),
    trackingNo: z.string().trim().max(80).optional(),
});
export const verifyScanSchema = z.object({
    scannedCode: z.string().trim().min(1).max(120),
});
export const shipSchema = z.object({});
export const bindTrackingSchema = z.object({
    scannedCode: z.string().trim().min(1).max(120),
});
export const voidRequestSchema = z.object({
    serial: digit8Schema,
    reason: z.string().trim().min(1).max(300),
});
export const passwordConfirmSchema = z.object({
    password: z.string().min(1).max(128),
});
export const rejectRequestSchema = z.object({
    reason: z.string().trim().min(1).max(300),
});
export const rehangSchema = z.object({
    scannedCode: z.string().trim().max(120).optional(),
    locationCode: locationCodeSchema.optional(),
    batchId: z.string().min(1).optional(),
    serial: digit8Schema.optional(),
    note: z.string().max(300).optional(),
});
export const returnVerifySchema = z.object({
    scannedCode: z.string().trim().min(1).max(120),
});
export const returnForeignSchema = z.object({
    note: z.string().trim().min(1).max(300),
    trackingNo: z.string().trim().max(80).optional(),
});
export const freezeSchema = z.object({
    reason: z.string().trim().min(1).max(80),
    note: z.string().max(300).optional(),
    password: z.string().min(1).max(128),
});
export const freezeRequestSchema = z.object({
    serial: digit8Schema,
    reason: z.string().trim().min(1).max(80),
    note: z.string().max(300).optional(),
});
export const stockCountCreateSchema = z.object({
    locationCode: locationCodeSchema,
});
export const stockCountScanSchema = z.object({
    scannedCode: z.string().trim().min(1).max(120),
});
export const adjustmentSchema = z.object({
    stockCountId: z.string().min(1).optional(),
    locationCode: locationCodeSchema.optional(),
    kind: z.enum(["SHORTAGE", "DAMAGE", "SURPLUS"]),
    note: z.string().trim().min(1).max(500),
    serials: z.array(digit8Schema).max(500),
});
export const reconciliationSchema = z.object({
    weekKey: z.string().trim().min(4).max(20),
    physicalQty: z.number().int().min(0),
    odooQty: z.number().int().min(0),
    reason: z.string().max(500).optional(),
    resolved: z.boolean(),
    resolution: z.string().max(500).optional(),
});
export const skuImportRowSchema = z.object({
    humanCode: z.string().trim().min(1),
    name: z.string().trim().min(1),
    color: z.string().trim().min(1),
    sizeUs: z.string().trim().min(1),
    sizeEu: z.string().trim().min(1),
    trackUnit: z.boolean(),
});
export const locationImportRowSchema = z.object({
    code: locationCodeSchema,
    capacity: z.number().int().min(1),
    status: z.enum(["NORMAL", "FULL", "LOCKED"]),
});
export const openingImportRowSchema = z.object({
    humanCode: z.string().trim().min(1),
    batchNo: z.string().trim().min(1),
    inboundDate: z.string().min(1),
    locationCode: locationCodeSchema,
    quantity: z.number().int().min(1),
});
export const arrivalImportRowSchema = z.object({
    humanCode: z.string().trim().min(1).max(64),
    plannedQty: z.number().int().min(1).max(100000),
    inboundDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
export const outboundImportRowSchema = z.object({
    humanCode: z.string().trim().min(1).max(64),
    requiredQty: z.number().int().min(1).max(100000),
    channel: z.string().trim().min(1).max(40),
    targetWarehouse: z.string().trim().min(1).max(40),
});
export const DOC_ROW_LIMIT = 5000;
export const importCommitSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("sku"),
        rows: z.array(skuImportRowSchema).max(20000),
    }),
    z.object({
        kind: z.literal("location"),
        rows: z.array(locationImportRowSchema).max(5000),
    }),
    z.object({
        kind: z.literal("opening"),
        rows: z.array(openingImportRowSchema).max(5000),
    }),
    z.object({
        kind: z.literal("arrival"),
        rows: z.array(arrivalImportRowSchema).max(DOC_ROW_LIMIT),
    }),
    z.object({
        kind: z.literal("outbound"),
        rows: z.array(outboundImportRowSchema).max(DOC_ROW_LIMIT),
    }),
]);
export const parseCodeSchema = z.object({
    raw: z.string().min(1).max(200),
});
//# sourceMappingURL=index.js.map